import { UploadStage } from './upload-error-messages'

const LogLevel = {
  DEBUG: 'DEBUG',
  INFO: 'INFO',
  WARN: 'WARN',
  ERROR: 'ERROR'
} as const

type LogLevel = typeof LogLevel[keyof typeof LogLevel]

export type LogContext = {
  stage?: UploadStage
  fileName?: string
  fileSize?: number
  documentId?: string
  surgicalOrderId?: number
  userId?: number
  attempt?: number
  maxRetries?: number
  timestamp?: string
  [key: string]: unknown
}

type ErrorInfo = {
  message: string
  name?: string
  code?: string | number
  stack?: string
  responseData?: unknown
}

const extractErrorInfo = (error: unknown): ErrorInfo => {
  if (error instanceof Error) {
    const candidate = error as Error & {
      code?: string | number
      response?: { status?: number; data?: unknown }
    }
    return {
      message: candidate.message,
      name: candidate.name,
      code: candidate.code ?? candidate.response?.status,
      stack: candidate.stack,
      responseData: candidate.response?.data
    }
  }

  if (typeof error === 'object' && error !== null) {
    const record = error as Record<string, unknown>
    const response = record.response as
      | { status?: number; data?: unknown }
      | undefined
    return {
      message:
        typeof record.message === 'string' ? record.message : String(error),
      name: typeof record.name === 'string' ? record.name : undefined,
      code: (record.code as string | number | undefined) ?? response?.status,
      stack: typeof record.stack === 'string' ? record.stack : undefined,
      responseData: response?.data
    }
  }

  return { message: String(error) }
}

export class UploadLogger {
  private enabled = true

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled
  }

  private formatMessage(
    level: LogLevel,
    message: string,
    context?: LogContext
  ): string {
    const timestamp = context?.timestamp || new Date().toISOString()
    const contextStr = context ? ` | Context: ${JSON.stringify(context)}` : ''
    return `[${timestamp}] [${level}] [Upload] ${message}${contextStr}`
  }

  public debug(message: string, context?: LogContext): void {
    if (!this.enabled) return
    console.debug(this.formatMessage(LogLevel.DEBUG, message, context))
  }

  public info(message: string, context?: LogContext): void {
    if (!this.enabled) return
    console.info(this.formatMessage(LogLevel.INFO, message, context))
  }

  public warn(message: string, context?: LogContext): void {
    if (!this.enabled) return
    console.warn(this.formatMessage(LogLevel.WARN, message, context))
  }

  public error(message: string, error: unknown, context?: LogContext): void {
    if (!this.enabled) return

    const errorInfo = extractErrorInfo(error)

    const errorContext: LogContext = {
      ...context,
      errorMessage: errorInfo.message,
      errorName: errorInfo.name,
      errorCode: errorInfo.code
    }

    console.error(this.formatMessage(LogLevel.ERROR, message, errorContext))

    if (errorInfo.stack) {
      console.error('Stack trace:', errorInfo.stack)
    }

    if (errorInfo.responseData) {
      console.error('Response data:', errorInfo.responseData)
    }
  }

  public logRetry(
    stage: UploadStage,
    attempt: number,
    maxRetries: number,
    error: unknown,
    context?: LogContext
  ): void {
    const { message } = extractErrorInfo(error)
    const retryContext: LogContext = {
      ...context,
      stage,
      attempt,
      maxRetries,
      errorMessage: message
    }

    this.warn(
      `Retry attempt ${attempt}/${maxRetries} for ${stage}`,
      retryContext
    )
  }

  public logUploadStart(
    fileName: string,
    fileSize: number,
    context?: LogContext
  ): void {
    this.info('Upload started', {
      ...context,
      fileName,
      fileSize,
      fileSizeMB: (fileSize / (1024 * 1024)).toFixed(2)
    })
  }

  public logUploadSuccess(
    fileName: string,
    documentId: string,
    duration: number,
    context?: LogContext
  ): void {
    this.info('Upload completed successfully', {
      ...context,
      fileName,
      documentId,
      durationMs: duration
    })
  }

  public logUploadFailure(
    fileName: string,
    error: unknown,
    context?: LogContext
  ): void {
    this.error('Upload failed', error, {
      ...context,
      fileName
    })
  }

  public logStageStart(stage: UploadStage, context?: LogContext): void {
    this.debug(`Stage started: ${stage}`, {
      ...context,
      stage
    })
  }

  public logStageSuccess(
    stage: UploadStage,
    duration: number,
    context?: LogContext
  ): void {
    this.debug(`Stage completed: ${stage}`, {
      ...context,
      stage,
      durationMs: duration
    })
  }

  public logStageFailure(
    stage: UploadStage,
    error: unknown,
    context?: LogContext
  ): void {
    this.error(`Stage failed: ${stage}`, error, {
      ...context,
      stage
    })
  }

  public logValidationError(
    fileName: string,
    reason: string,
    context?: LogContext
  ): void {
    this.warn('File validation failed', {
      ...context,
      stage: UploadStage.VALIDATION,
      fileName,
      reason
    })
  }

  public logPresignedUrlRequest(
    fileName: string,
    documentType: string,
    context?: LogContext
  ): void {
    this.debug('Requesting presigned URL', {
      ...context,
      stage: UploadStage.REQUEST_URL,
      fileName,
      documentType
    })
  }

  public logPresignedUrlReceived(
    documentId: string,
    context?: LogContext
  ): void {
    this.debug('Presigned URL received', {
      ...context,
      stage: UploadStage.REQUEST_URL,
      documentId
    })
  }

  public logS3UploadStart(
    fileName: string,
    fileSize: number,
    context?: LogContext
  ): void {
    this.debug('Starting S3 upload', {
      ...context,
      stage: UploadStage.S3_UPLOAD,
      fileName,
      fileSize
    })
  }

  public logS3UploadSuccess(fileName: string, context?: LogContext): void {
    this.debug('S3 upload successful', {
      ...context,
      stage: UploadStage.S3_UPLOAD,
      fileName
    })
  }

  public logNotificationStart(documentId: string, context?: LogContext): void {
    this.debug('Notifying backend', {
      ...context,
      stage: UploadStage.NOTIFICATION,
      documentId
    })
  }

  public logNotificationSuccess(
    documentId: string,
    context?: LogContext
  ): void {
    this.debug('Backend notification successful', {
      ...context,
      stage: UploadStage.NOTIFICATION,
      documentId
    })
  }

  public logMultiUploadProgress(
    completed: number,
    total: number,
    context?: LogContext
  ): void {
    this.info(`Multi-upload progress: ${completed}/${total}`, {
      ...context,
      completed,
      total,
      percentage: Math.round((completed / total) * 100)
    })
  }

  public logUploadCancelled(fileName: string, context?: LogContext): void {
    this.warn('Upload cancelled by user', {
      ...context,
      fileName
    })
  }
}

/**
 * Instância default compartilhada do logger.
 * Pode ser substituída em testes criando uma nova instância de `UploadLogger`.
 */
export const defaultUploadLogger = new UploadLogger()

export function getUploadLogger(): UploadLogger {
  return defaultUploadLogger
}

type BoundUploadLogger = {
  debug: (message: string, context?: LogContext) => void
  info: (message: string, context?: LogContext) => void
  warn: (message: string, context?: LogContext) => void
  error: (message: string, error: unknown, context?: LogContext) => void
  logRetry: (
    stage: UploadStage,
    attempt: number,
    maxRetries: number,
    error: unknown,
    context?: LogContext
  ) => void
  logUploadStart: (
    fileName: string,
    fileSize: number,
    context?: LogContext
  ) => void
  logUploadSuccess: (
    fileName: string,
    documentId: string,
    duration: number,
    context?: LogContext
  ) => void
  logUploadFailure: (
    fileName: string,
    error: unknown,
    context?: LogContext
  ) => void
  logStageStart: (stage: UploadStage, context?: LogContext) => void
  logStageSuccess: (
    stage: UploadStage,
    duration: number,
    context?: LogContext
  ) => void
  logStageFailure: (
    stage: UploadStage,
    error: unknown,
    context?: LogContext
  ) => void
  logValidationError: (
    fileName: string,
    reason: string,
    context?: LogContext
  ) => void
  logPresignedUrlRequest: (
    fileName: string,
    documentType: string,
    context?: LogContext
  ) => void
  logPresignedUrlReceived: (documentId: string, context?: LogContext) => void
  logS3UploadStart: (
    fileName: string,
    fileSize: number,
    context?: LogContext
  ) => void
  logS3UploadSuccess: (fileName: string, context?: LogContext) => void
  logNotificationStart: (documentId: string, context?: LogContext) => void
  logNotificationSuccess: (documentId: string, context?: LogContext) => void
  logMultiUploadProgress: (
    completed: number,
    total: number,
    context?: LogContext
  ) => void
  logUploadCancelled: (fileName: string, context?: LogContext) => void
}

export function createUploadLogger(
  defaultContext: LogContext
): BoundUploadLogger {
  const logger = getUploadLogger()

  return {
    debug: (message, context) =>
      logger.debug(message, { ...defaultContext, ...context }),
    info: (message, context) =>
      logger.info(message, { ...defaultContext, ...context }),
    warn: (message, context) =>
      logger.warn(message, { ...defaultContext, ...context }),
    error: (message, error, context) =>
      logger.error(message, error, { ...defaultContext, ...context }),
    logRetry: (stage, attempt, maxRetries, error, context) =>
      logger.logRetry(stage, attempt, maxRetries, error, {
        ...defaultContext,
        ...context
      }),
    logUploadStart: (fileName, fileSize, context) =>
      logger.logUploadStart(fileName, fileSize, {
        ...defaultContext,
        ...context
      }),
    logUploadSuccess: (fileName, documentId, duration, context) =>
      logger.logUploadSuccess(fileName, documentId, duration, {
        ...defaultContext,
        ...context
      }),
    logUploadFailure: (fileName, error, context) =>
      logger.logUploadFailure(fileName, error, {
        ...defaultContext,
        ...context
      }),
    logStageStart: (stage, context) =>
      logger.logStageStart(stage, { ...defaultContext, ...context }),
    logStageSuccess: (stage, duration, context) =>
      logger.logStageSuccess(stage, duration, {
        ...defaultContext,
        ...context
      }),
    logStageFailure: (stage, error, context) =>
      logger.logStageFailure(stage, error, { ...defaultContext, ...context }),
    logValidationError: (fileName, reason, context) =>
      logger.logValidationError(fileName, reason, {
        ...defaultContext,
        ...context
      }),
    logPresignedUrlRequest: (fileName, documentType, context) =>
      logger.logPresignedUrlRequest(fileName, documentType, {
        ...defaultContext,
        ...context
      }),
    logPresignedUrlReceived: (documentId, context) =>
      logger.logPresignedUrlReceived(documentId, {
        ...defaultContext,
        ...context
      }),
    logS3UploadStart: (fileName, fileSize, context) =>
      logger.logS3UploadStart(fileName, fileSize, {
        ...defaultContext,
        ...context
      }),
    logS3UploadSuccess: (fileName, context) =>
      logger.logS3UploadSuccess(fileName, { ...defaultContext, ...context }),
    logNotificationStart: (documentId, context) =>
      logger.logNotificationStart(documentId, {
        ...defaultContext,
        ...context
      }),
    logNotificationSuccess: (documentId, context) =>
      logger.logNotificationSuccess(documentId, {
        ...defaultContext,
        ...context
      }),
    logMultiUploadProgress: (completed, total, context) =>
      logger.logMultiUploadProgress(completed, total, {
        ...defaultContext,
        ...context
      }),
    logUploadCancelled: (fileName, context) =>
      logger.logUploadCancelled(fileName, { ...defaultContext, ...context })
  }
}
